import {
  Broadcast, Play, Pause, SpeakerHigh, SpeakerLow, SpeakerSlash,
} from "@phosphor-icons/react";

const cn = (...a) => a.filter(Boolean).join(" ");

function VolumeIcon({ muted, volume }) {
  if (muted) return <SpeakerSlash size={18} />;
  if (volume > 0.5) return <SpeakerHigh size={18} />;
  return <SpeakerLow size={18} />;
}

function StatusText({ isPlaying }) {
  if (isPlaying) return <span className="text-[#FF2A00]">● LIVE STREAM</span>;
  return <span className="text-[#A1A1AA]">○ STAND-BY</span>;
}

export default function PlayerBar({ station, isPlaying, onToggle, volume, muted, onVolume, onMute }) {
  const stationLabel = station?.name || "Wähle einen Sender →";
  const stationMeta = station ? `${station.bitrate || 0}K · ${station.codec || "audio"}` : "—";

  return (
    <footer className="h-24 border-t-2 border-[#18181B] flex items-center bg-white shrink-0" data-testid="player-bar">
      {/* Left: Station info */}
      <div className="flex items-center gap-3 md:gap-4 px-4 md:px-6 w-1/2 md:w-80 md:border-r border-[#E4E4E7] h-full min-w-0">
        <div className="w-12 h-12 md:w-14 md:h-14 border border-[#E4E4E7] flex items-center justify-center overflow-hidden bg-white shrink-0">
          {station?.favicon ? (
            <img
              alt=""
              src={station.favicon}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <Broadcast size={24} weight="duotone" className="text-[#A1A1AA]" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="overline hidden sm:block">{station ? "JETZT LÄUFT" : "KEIN SENDER"}</div>
          <div className="font-display font-extrabold tracking-tight text-sm md:text-base truncate">{stationLabel}</div>
          <div className="font-mono-tech text-[0.6rem] md:text-[0.65rem] text-[#52525B] truncate">{stationMeta}</div>
        </div>
      </div>

      {/* Center: Controls + wave */}
      <div className="flex-1 flex items-center justify-center gap-3 md:gap-6 px-3 md:px-6 min-w-0">
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          disabled={!station}
          data-testid="play-pause-btn"
          className={cn(
            "w-12 h-12 md:w-14 md:h-14 shrink-0 flex items-center justify-center border-2 transition-colors",
            station
              ? "border-[#18181B] bg-[#18181B] text-white hover:bg-[#FF2A00] hover:border-[#FF2A00]"
              : "border-[#E4E4E7] text-[#A1A1AA] cursor-not-allowed"
          )}
        >
          {isPlaying ? <Pause size={22} weight="fill" /> : <Play size={22} weight="fill" />}
        </button>
        <div className={cn("wave hidden sm:flex", isPlaying && "playing")} aria-hidden>
          <span /><span /><span /><span /><span /><span />
        </div>
        <div className="font-mono-tech text-[0.65rem] md:text-[0.7rem] tracking-[0.15em] hidden sm:block">
          <StatusText isPlaying={isPlaying} />
        </div>
      </div>

      {/* Right: Volume (hidden on small mobile) */}
      <div className="hidden md:flex w-72 px-6 border-l border-[#E4E4E7] h-full items-center gap-3">
        <button onClick={onMute} data-testid="mute-btn" className="p-1.5 hover:bg-[#F4F4F5]">
          <VolumeIcon muted={muted} volume={volume} />
        </button>
        <input
          type="range"
          min={0} max={1} step={0.01}
          value={muted ? 0 : volume}
          onChange={(e) => {
            onVolume(parseFloat(e.target.value));
            if (muted) onMute();
          }}
          className="flex-1 accent-[#18181B]"
          data-testid="volume-slider"
        />
        <span className="font-mono-tech text-[0.65rem] text-[#52525B] w-8 text-right">
          {Math.round((muted ? 0 : volume) * 100)}
        </span>
      </div>

      {/* Mute toggle on mobile only */}
      <button
        onClick={onMute}
        data-testid="mute-btn-mobile"
        className="md:hidden p-2 mr-3 hover:bg-[#F4F4F5] border border-[#E4E4E7] shrink-0"
        aria-label="Toggle mute"
      >
        <VolumeIcon muted={muted} volume={volume} />
      </button>
    </footer>
  );
}
